import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { SessionStorage } from 'common/CookieStorage'
import { STORAGEKEYS } from 'enviroment/Keys'
import { IUseMObywatelPageLogicReturnProps } from '../MObywatelIdentityPageProps'
import mObywatelService from 'service/MObywatelService/MObywatelService'
import { AppState } from 'store'
import { DateTime } from 'luxon'


/**
 * Hook z logiką obsługujący stronę z kodem mObywatela
*/
export const useMObywatelPageLogic = (isIdentityPage: boolean): IUseMObywatelPageLogicReturnProps => {
  const sessionStorage = new SessionStorage()
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null)
  const MOBYWATEL_CODE_VALIDATION_TIME_IN_SECONDS = useSelector((state: AppState) => state.globalConfigurationState.globalConfig.mObywatelCodeValidationTimeInSeconds)


  /** USE STATE 
   * W momencie odświeżenia strony, sprawdzamy czy w sessionStorage mamy zapisany aktualnie kod mObywatela.
   * Jeśli tak ustawiamy ten kod. Jeśli jest on pusty, oznacza to że obecnie żaden kod mObywatela nie jest aktywny, więc zwracamy pusty string.
   *  
  */
  const [currentMObywatelCode, setCurrentMObywatelCode] = useState(() => {
    const currentCode = sessionStorage.get(STORAGEKEYS.CURRENT_MOBYWATEL_CODE)
    return (currentCode && currentCode.trim().length !== 0) ? currentCode : ""
  })


  /** USE STATE 
   * W momencie odświeżenia strony, sprawdzamy w zmiennej z sessionStorage, przez jaki czas kod mObywatela jest jeszcze aktywny.
   * Jeśli takiej zmiennej nie ma jeszcze ustawionej, oznacza to pierwszy render strony. Wtedy ustawiamy domyślną wartość i startujemy odliczanie do 0
   *  
  */
  const [timeToCodeExpiresInSeconds, setTimeToCodeExpiresInSeconds] = useState(() => {
    const savedSeconds = sessionStorage.get(STORAGEKEYS.MOBYWATEL_CODE_VALIDITY_STARTING_TIME)
    return savedSeconds ? parseInt(savedSeconds, 10) : MOBYWATEL_CODE_VALIDATION_TIME_IN_SECONDS
  })



  /** FUNKCJA 
   * Funkcja używana do obsługi pętli odliczającej do zakończenia czasu aktywności kodu mObywatela.
   * Uruchamia ona interwał, który wykonuje się co sekudnę i odlicza w dół czas ważności kodu.
   * Kod pobierany jest z sessionStorage, tak aby odświeżanie strony nie miało wpływu na resetowanie się timera.
   * 
   * @param hasFirstRender 
   */
  const startOrContinueCodeInterval = (hasFirstRender: boolean): void => {
    !hasFirstRender && sessionStorage.add(STORAGEKEYS.MOBYWATEL_IS_AFTER_FIRST_RENDER, 'true') // Od razu przed pierwszym uruchomieniem timera, ustawiamy zmienną mówiącą o tym że pierwszy render strony miał już miejsce 

    const id = setInterval(() => {
      setTimeToCodeExpiresInSeconds((prevSeconds) => {
        if (prevSeconds > 0) {
          sessionStorage.add(STORAGEKEYS.MOBYWATEL_CODE_VALIDITY_STARTING_TIME,  (prevSeconds - 1).toString())
          return prevSeconds - 1
        } 
        else {
          // CLEANUP
          clearInterval(id)
          sessionStorage.add(STORAGEKEYS.CURRENT_MOBYWATEL_CODE, "")
          sessionStorage.add(STORAGEKEYS.MOBYWATEL_CODE_VALIDITY_STARTING_TIME, "0")
          setCurrentMObywatelCode("")
          return 0
        }
      })
    }, 1000)

    setIntervalId(id)
  }


  /** FUNKCJA 
   * Funkcja pobierająca nowy kod mObywatela z Backendu, zapisująca go w sessionStorage
   * oraz startująca timer
   * @param hasFirstRender 
   */
  const generateNewMObywatelCode = async(hasFirstRender: boolean) => { //GENEROWANIE KODU QR ORAZ URUCHAMIANIE TIMERA
    const newMObywatelCode = await mObywatelService.getMobywatelQRCode(isIdentityPage) 

    const from = DateTime.fromISO(newMObywatelCode.dateValidity)
    const timeDiff = from.diff(DateTime.utc(), 'seconds').seconds ?? MOBYWATEL_CODE_VALIDATION_TIME_IN_SECONDS
    setTimeToCodeExpiresInSeconds(timeDiff)

    sessionStorage.add(STORAGEKEYS.CURRENT_MOBYWATEL_CODE, newMObywatelCode.text)
    setCurrentMObywatelCode(newMObywatelCode.text)
    startOrContinueCodeInterval(hasFirstRender)

    return newMObywatelCode
  }

  /** FUNKCJA 
   * Funkcja pomocnicza uruchamiana po wykonaniu akcji w UI (naciśnięciu przycisku)
   * Pierwsze jej uruchomienie jest możliwe dopiero po wygaśnięciu kodu, pojawiającego się przy pierwszym renderze strony, 
   */
  const regenerateMObywatelCode = () => { // PONOWNE GENEROWANIE KOU QR Z PRZYCISKU
    if(currentMObywatelCode.trim().length === 0) {
      generateNewMObywatelCode(false)
    }
  }


  /** HOOK
   * Hook obsługujący pierwszy render strony, oraz odświeżanie strony.
  */
  useEffect(() => {
    const hasFirstRender = Boolean(sessionStorage.get(STORAGEKEYS.MOBYWATEL_IS_AFTER_FIRST_RENDER) === 'true')

    if(hasFirstRender){ // KAŻDE KOLEJNE ODŚWIEŻENIE STRONY
      startOrContinueCodeInterval(hasFirstRender)
    }
    else { // PIERWSZE WEJŚCIE NA STRONĘ - PO RAZ PIERWSZY GENERUJEMY KOD MOBYWATELA
      generateNewMObywatelCode(hasFirstRender)
    }

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }

  // eslint-disable-line react-hooks/exhaustive-deps
  }, [])


  return { 
    timeToCodeExpiresInSeconds: timeToCodeExpiresInSeconds, 
    isMobywatelCodeActive: Boolean(currentMObywatelCode.trim().length),
    mObywatelCurrentlyActiveCode: currentMObywatelCode,

    regenerateMObywatelCode
  };
};

